import React, { useState, useRef } from 'react';
import { X, Upload, Trash2, Eye, Settings } from 'lucide-react';
import Button from '../common/Button';
import { useToastContext } from '../../context/ToastContext';
import { eventAPI } from '../../api/endpoints';
import { getConsistentPreviewData } from '../../utils/badgePreviewData';
import BadgePreview from './BadgePreview';
import ConfirmModal from '../common/ConfirmModal';

const LogoConfigurationModal = ({ 
  isOpen, 
  onClose, 
  event, 
  onLogoUpdated 
}) => {
  const toast = useToastContext();
  const fileInputRef = useRef(null);
  
  const [loading, setLoading] = useState(false);
  const [logoFile, setLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [config, setConfig] = useState({
    logo_position: event?.logo_position || 'top-center',
    logo_shape: event?.logo_shape || 'circular',
    logo_size: event?.logo_size || 60,
    show_logo: event?.show_logo || false,
    show_registration_number: event?.show_registration_number || false,
    registration_number_format: event?.registration_number_format || 'REG-{id}',
    show_attendee_title: event?.show_attendee_title ?? true,
    show_company_name: event?.show_company_name ?? true,
  });

  if (!isOpen) return null;

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file type
      if (!file.type.startsWith('image/')) {
        toast.error('Please select an image file');
        return;
      }
      
      // Validate file size (2MB)
      if (file.size > 2 * 1024 * 1024) {
        toast.error('File size must be less than 2MB');
        return;
      }

      setLogoFile(file);
      
      // Create preview URL
      const reader = new FileReader();
      reader.onload = (e) => {
        setLogoPreview(e.target.result);
      };
      reader.readAsDataURL(file);
    }
  };


  const handleDeleteLogo = async () => {
    setShowDeleteConfirm(true);
  };
  
  const confirmDeleteLogo = async () => {
    setShowDeleteConfirm(false);

    setLoading(true);
    try {
      await eventAPI.deleteLogo(event.id);
      toast.success('Logo deleted successfully!');
      onLogoUpdated?.();
    } catch (error) {
      console.error('Logo delete error:', error);
      toast.error(error.response?.data?.message || 'Failed to delete logo');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveConfiguration = async () => {
    setLoading(true);
    try {
      // If there's a new logo file, upload it first
      if (logoFile) {
        const formData = new FormData();
        formData.append('logo', logoFile);
        formData.append('logo_position', config.logo_position);
        formData.append('logo_shape', config.logo_shape);
        formData.append('logo_size', config.logo_size);

        await eventAPI.uploadLogo(event.id, formData);
        toast.success('Logo uploaded and configuration saved successfully!');
      } else {
        // Just update configuration if no new logo
        await eventAPI.updateLogoConfig(event.id, config);
        toast.success('Configuration updated successfully!');
      }
      
      // Clear the selected file and preview
      setLogoFile(null);
      setLogoPreview(null);
      onLogoUpdated?.();
      
    } catch (error) {
      console.error('Save configuration error:', error);
      
      const errorMessage = error.response?.data?.message || 
                          error.response?.data?.error || 
                          error.message || 
                          'Failed to save configuration';
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleConfigChange = (key, value) => {
    setConfig(prev => ({ ...prev, [key]: value }));
  };

  const currentLogoUrl = logoPreview || event?.logo_url;

  return (
    <>
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">Logo Configuration</h2>
            <p className="text-gray-600 mt-1">Customize logo and badge display settings for {event?.name}</p>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <X className="h-6 w-6 text-gray-500" />
          </button>
        </div>

        <div className="p-6 grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Configuration Panel */}
          <div className="space-y-6">
            {/* Logo Upload Section */}
            <div className="space-y-4">
              <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                <Upload className="h-5 w-5" />
                Logo Upload
              </h3>
              
              {/* Current Logo Display */}
              {event?.logo_url && !logoPreview && (
                <div className="flex items-center gap-4 p-4 bg-gray-50 rounded-lg">
                  <img 
                    src={event.logo_url} 
                    alt="Current Logo" 
                    className="h-16 w-16 object-cover rounded-lg shadow-md"
                  />
                  <div className="flex-1">
                    <p className="text-sm font-medium text-gray-900">Current Logo</p>
                    <p className="text-xs text-gray-500">Click "Choose File" to replace</p>
                  </div>
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={handleDeleteLogo}
                    className="gap-2"
                    loading={loading}
                  >
                    <Trash2 className="h-4 w-4" />
                    Delete
                  </Button>
                </div>
              )}

              {/* File Upload */}
              <div className="space-y-3">
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handleFileSelect}
                  className="hidden"
                />
                
                <Button
                  variant="secondary"
                  onClick={() => fileInputRef.current?.click()}
                  className="w-full gap-2"
                >
                  <Upload className="h-4 w-4" />
                  Choose Logo File
                </Button>

                {logoPreview && (
                  <div className="flex items-center gap-4 p-4 bg-blue-50 rounded-lg border border-blue-200">
                    <img 
                      src={logoPreview} 
                      alt="Logo Preview" 
                      className="h-16 w-16 object-cover rounded-lg shadow-md"
                    />
                    <div className="flex-1">
                      <p className="text-sm font-medium text-blue-900">New Logo Selected</p>
                      <p className="text-xs text-blue-600">{logoFile?.name}</p>
                    </div>
                  </div>
                )}

                <p className="text-xs text-gray-500">
                  Supported formats: PNG, JPG, JPEG, SVG. Max size: 2MB. Recommended: 200x200px<br/>
                  <span className="text-blue-600 font-medium">Logo will be uploaded when you click "Save Configuration"</span>
                </p>
              </div>
            </div>

            {/* Logo Configuration */}
            <div className="space-y-4">
              <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                <Settings className="h-5 w-5" />
                Logo Settings
              </h3>

              {/* Logo Position */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Logo Position
                </label>
                <div className="grid grid-cols-3 gap-2">
                  {[
                    { value: 'top-left', label: 'Top Left' },
                    { value: 'top-center', label: 'Top Center' },
                    { value: 'top-right', label: 'Top Right' },
                  ].map((position) => (
                    <button
                      key={position.value}
                      onClick={() => handleConfigChange('logo_position', position.value)}
                      className={`p-3 text-sm font-medium rounded-lg border transition-colors ${
                        config.logo_position === position.value
                          ? 'bg-primary-50 border-primary-500 text-primary-700'
                          : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
                      }`}
                    >
                      {position.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Logo Shape */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Logo Shape
                </label>
                <div className="grid grid-cols-2 gap-2">
                  {[
                    { value: 'circular', label: 'Circular' },
                    { value: 'square', label: 'Square' },
                  ].map((shape) => (
                    <button
                      key={shape.value}
                      onClick={() => handleConfigChange('logo_shape', shape.value)}
                      className={`p-3 text-sm font-medium rounded-lg border transition-colors ${
                        config.logo_shape === shape.value
                          ? 'bg-primary-50 border-primary-500 text-primary-700'
                          : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
                      }`}
                    >
                      {shape.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Logo Size */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Logo Size: {config.logo_size}px
                </label>
                <input
                  type="range"
                  min="40"
                  max="70"
                  value={config.logo_size}
                  onChange={(e) => handleConfigChange('logo_size', parseInt(e.target.value))}
                  className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
                />
                <div className="flex justify-between text-xs text-gray-500 mt-1">
                  <span>40px</span>
                  <span>70px</span>
                </div>
              </div>
            </div>

            {/* Badge Display Options */}
            <div className="space-y-4">
              <h3 className="text-lg font-semibold text-gray-900">Badge Display Options</h3>
              
              <div className="space-y-3">
                {[
                  { key: 'show_logo', label: 'Show Logo on Badge', description: 'Display the event logo on badges' },
                  { key: 'show_attendee_title', label: 'Show Attendee Title', description: 'Display job title above attendee name' },
                  { key: 'show_company_name', label: 'Show Company Name', description: 'Display company/organization name' },
                  { key: 'show_registration_number', label: 'Show Registration Number', description: 'Display unique registration number' },
                ].map((option) => (
                  <label key={option.key} className="flex items-start gap-3 p-3 border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={config[option.key]}
                      onChange={(e) => handleConfigChange(option.key, e.target.checked)}
                      className="h-4 w-4 text-primary-600 rounded border-gray-300 focus:ring-primary-500 mt-0.5"
                    />
                    <div>
                      <div className="text-sm font-medium text-gray-900">{option.label}</div>
                      <div className="text-xs text-gray-500">{option.description}</div>
                    </div>
                  </label>
                ))}
              </div>

              {/* Registration Number Format */}
              {config.show_registration_number && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Registration Number Format
                  </label>
                  <input
                    type="text"
                    value={config.registration_number_format}
                    onChange={(e) => handleConfigChange('registration_number_format', e.target.value)}
                    placeholder="REG-{id}"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Use {'{id}'} as placeholder for attendee ID. Example: REG-{'{id}'} becomes REG-0001
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* Preview Panel */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
              <Eye className="h-5 w-5" />
              Live Preview
            </h3>
            
            <div className="bg-gray-50 rounded-lg p-6 flex items-center justify-center min-h-[500px]">
              <BadgePreview
                layout="standard"
                showQR={true}
                showLogo={config.show_logo}
                data={getConsistentPreviewData(event?.name)}
                logoConfig={{
                  logo_url: currentLogoUrl,
                  logo_position: config.logo_position,
                  logo_shape: config.logo_shape,
                  logo_size: config.logo_size,
                  show_registration_number: config.show_registration_number,
                  show_attendee_title: config.show_attendee_title,
                  show_company_name: config.show_company_name,
                }}
              />
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 p-6 border-t bg-gray-50">
          <Button
            variant="secondary"
            onClick={onClose}
          >
            Cancel
          </Button>
          
          <Button
            onClick={handleSaveConfiguration}
            loading={loading}
            className="gap-2"
          >
            <Settings className="h-4 w-4" />
            {logoFile ? 'Upload Logo & Save Configuration' : 'Save Configuration'}
          </Button>
        </div>
      </div>
    </div>
      
    <ConfirmModal
        isOpen={showDeleteConfirm}
        onClose={() => setShowDeleteConfirm(false)}
        onConfirm={confirmDeleteLogo}
        title="Delete Logo"
        message="Are you sure you want to delete the current logo? This action cannot be undone."
        confirmText="Delete Logo"
        cancelText="Cancel"
        variant="danger"
      />
    </>
  );
};

export default LogoConfigurationModal;
